import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { GlassCard, GhostButton, PrimaryButton } from "@/components/Primitives";
import Modal, { inputCls, Field } from "@/components/Modal";
import { useConfirm } from "@/components/ConfirmProvider";
import {
  Plus, Webhook, Copy, Eye, EyeOff, Trash2, Edit3, X,
  CheckCircle2, AlertCircle, Loader2, RefreshCw, Send, History,
} from "lucide-react";
import { toast } from "sonner";

function fmtDt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

function StatusBadge({ hook }) {
  if (!hook.last_delivery_at) {
    return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-white/[0.04] text-white/45 border-white/10">Never fired</span>;
  }
  if (hook.last_delivery_success) {
    return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-emerald-400/10 text-emerald-300 border-emerald-400/20">Healthy · {hook.last_delivery_status}</span>;
  }
  return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-rose-400/10 text-rose-300 border-rose-400/20">Failing · {hook.last_delivery_status || "err"}</span>;
}

function WebhookDialog({ open, initial, events, onClose, onSaved }) {
  const blank = { name: "", url: "", events: ["*"], enabled: true };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        name: initial.name || "",
        url: initial.url || "",
        events: Array.isArray(initial.events) ? initial.events : ["*"],
        enabled: initial.enabled !== false,
      });
    } else setForm(blank);
    setShowSecret(false);
  }, [open, initial]); // eslint-disable-line

  const toggleEvent = (ev) => {
    setForm((f) => {
      const set = new Set(f.events);
      // Selecting "*" replaces other selections; selecting a specific event clears "*"
      if (ev === "*") return { ...f, events: ["*"] };
      set.delete("*");
      if (set.has(ev)) set.delete(ev); else set.add(ev);
      return { ...f, events: Array.from(set.size ? set : new Set(["*"])) };
    });
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (isEdit) await api.patch(`/webhooks/${initial.id}`, payload);
      else await api.post("/webhooks", payload);
      toast.success(isEdit ? "Webhook saved" : "Webhook created");
      onClose(); onSaved();
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };

  const rotateSecret = async () => {
    try {
      const { data } = await api.post(`/webhooks/${initial.id}/rotate-secret`);
      toast.success("Secret rotated");
      onSaved();
      // Display the new secret in this dialog
      initial.secret = data.secret;
      setShowSecret(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <form onSubmit={submit} className="p-8" data-testid="webhook-dialog">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#007AFF]/80">Outbound webhook</div>
            <h2 className="font-display text-2xl font-light mt-1">{isEdit ? form.name || "Edit webhook" : "New webhook"}</h2>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <Field label="Name"><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Zapier — Slack notifier" data-testid="wh-name" /></Field>
          <Field label="Endpoint URL"><input required type="url" className={inputCls} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://hooks.zapier.com/..." data-testid="wh-url" /></Field>
        </div>

        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Events to listen for</div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => toggleEvent("*")}
              data-testid="wh-evt-all"
              className={`px-3 py-1.5 rounded-full text-xs border transition ${form.events.includes("*") ? "bg-[#007AFF] text-white border-[#007AFF]" : "bg-white/[0.04] text-white/65 border-white/10 hover:bg-white/[0.08]"}`}>
              All events
            </button>
            {events.map((ev) => (
              <button key={ev} type="button" onClick={() => toggleEvent(ev)}
                data-testid={`wh-evt-${ev}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition ${form.events.includes(ev) ? "bg-[#007AFF]/15 text-[#7ebbff] border-[#007AFF]/40" : "bg-white/[0.04] text-white/55 border-white/10 hover:bg-white/[0.08]"}`}>
                {ev}
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-3 mb-6 cursor-pointer select-none">
          <div className={`relative w-10 h-6 rounded-full transition ${form.enabled ? "bg-[#007AFF]" : "bg-white/[0.1]"}`}>
            <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition ${form.enabled ? "translate-x-4" : ""}`} />
          </div>
          <input type="checkbox" className="sr-only" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="wh-enabled" />
          <span className="text-sm text-white/80">Enabled</span>
        </label>

        {isEdit && initial.secret && (
          <div className="mb-6 p-4 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Signing secret</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs text-white/80 font-mono truncate" data-testid="wh-secret">
                {showSecret ? initial.secret : "•".repeat(40)}
              </code>
              <button type="button" onClick={() => setShowSecret((s) => !s)} className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.1]" title={showSecret ? "Hide" : "Reveal"}>
                {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <button type="button" onClick={() => copy(initial.secret)} className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.1]" title="Copy"><Copy className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={rotateSecret} className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.1]" title="Rotate"><RefreshCw className="w-3.5 h-3.5" /></button>
            </div>
            <div className="text-[11px] text-white/40 mt-2 leading-relaxed">
              Each request is signed with HMAC-SHA256 over the raw body. Verify the <code className="text-white/70">X-Equinox-Signature</code> header using this secret.
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton type="submit" disabled={busy} data-testid="wh-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isEdit ? "Save" : "Create webhook"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

function DeliveriesDialog({ open, hook, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!hook?.id) return;
    setLoading(true);
    try { const { data } = await api.get(`/webhooks/${hook.id}/deliveries`); setItems(data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open && hook) load(); }, [open, hook]); // eslint-disable-line

  if (!open || !hook) return null;
  return (
    <Modal open={open} onClose={onClose} size="xl">
      <div className="p-8" data-testid="deliveries-dialog">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#007AFF]/80">Delivery history</div>
            <h2 className="font-display text-2xl font-light mt-1">{hook.name}</h2>
            <div className="text-xs text-white/45 mt-1 font-mono">{hook.url}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        {loading && <div className="text-white/40 text-center py-10">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-white/40 text-center py-10">No deliveries yet — events will appear here once a matching event fires.</div>}

        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center gap-4 text-xs" data-testid={`delivery-${d.id}`}>
              {d.success
                ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                : <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-white truncate">{d.event}</div>
                {d.error && <div className="text-rose-300/80 truncate mt-0.5">{d.error}</div>}
                {!d.error && d.response_body && <div className="text-white/40 truncate mt-0.5 font-mono">{d.response_body}</div>}
              </div>
              <div className="text-white/45 shrink-0">{d.status_code || "—"}</div>
              <div className="text-white/40 shrink-0 w-16 text-right">{d.duration_ms}ms</div>
              <div className="text-white/40 shrink-0">{fmtDt(d.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

export default function WebhooksSection() {
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deliveriesFor, setDeliveriesFor] = useState(null);
  const [testing, setTesting] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    const [h, e] = await Promise.all([api.get("/webhooks"), api.get("/webhooks/events")]);
    setHooks(h.data); setEvents(e.data.events || []);
  };
  useEffect(() => { load(); }, []);

  const test = async (hook) => {
    setTesting(hook.id);
    try {
      const { data } = await api.post(`/webhooks/${hook.id}/test`);
      if (data.success) toast.success(`Test sent · ${data.status_code} in ${data.duration_ms}ms`);
      else toast.error(`Test failed · ${data.error || data.status_code}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setTesting(null); }
  };

  const remove = async (hook) => {
    const ok = await confirm({
      title: "Delete this webhook?",
      description: `“${hook.name}” will stop receiving events. Delivery history will be cleared.`,
      confirmLabel: "Delete webhook",
    });
    if (!ok) return;
    try { await api.delete(`/webhooks/${hook.id}`); toast.success("Webhook deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <GlassCard className="lg:col-span-2" testId="settings-webhooks">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-white/[0.05] flex items-center justify-center">
            <Webhook className="w-4 h-4 text-[#007AFF]" />
          </div>
          <div>
            <div className="font-display text-xl">Webhooks</div>
            <div className="text-xs text-white/45 mt-0.5">Send domain events to Zapier, Make, n8n, or your own services.</div>
          </div>
        </div>
        <PrimaryButton onClick={() => { setEditing(null); setDlgOpen(true); }} data-testid="new-webhook-btn">
          <Plus className="w-4 h-4" /> New webhook
        </PrimaryButton>
      </div>

      {hooks.length === 0 && (
        <div className="text-center py-10 text-white/45 text-sm border border-dashed border-white/[0.08] rounded-2xl">
          No webhooks yet. Click <span className="text-white">New webhook</span> to create one.
        </div>
      )}

      <div className="space-y-2">
        {hooks.map((h) => (
          <div key={h.id} className="p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center gap-4" data-testid={`webhook-row-${h.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <div className="text-white font-medium truncate">{h.name}</div>
                <StatusBadge hook={h} />
                {!h.enabled && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-white/[0.04] text-white/40 border-white/10">Disabled</span>}
              </div>
              <div className="text-xs text-white/45 font-mono truncate mt-1">{h.url}</div>
              <div className="text-[11px] text-white/40 mt-1">
                {(h.events || []).slice(0, 4).join(" · ")}
                {(h.events || []).length > 4 && ` · +${(h.events || []).length - 4} more`}
                {h.last_delivery_at && <span> · last fired {fmtDt(h.last_delivery_at)}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => test(h)} disabled={testing === h.id}
                className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.1] text-white/70 hover:text-white transition disabled:opacity-50"
                data-testid={`wh-test-${h.id}`} title="Send test event">
                {testing === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => setDeliveriesFor(h)}
                className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.1] text-white/70 hover:text-white transition"
                data-testid={`wh-history-${h.id}`} title="Delivery history">
                <History className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => { setEditing(h); setDlgOpen(true); }}
                className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.1] text-white/70 hover:text-white transition"
                data-testid={`wh-edit-${h.id}`} title="Edit">
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => remove(h)}
                className="p-2 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 transition"
                data-testid={`wh-del-${h.id}`} title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <WebhookDialog open={dlgOpen} initial={editing} events={events} onClose={() => setDlgOpen(false)} onSaved={load} />
      <DeliveriesDialog open={!!deliveriesFor} hook={deliveriesFor} onClose={() => setDeliveriesFor(null)} />
    </GlassCard>
  );
}
